// src/pages/CRM/SFA/SalesTargets.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Avatar,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { salesTargetService } from './sfaService';
import {
  TargetPeriod,
  TARGET_PERIOD_LABELS,
  formatCurrency,
  formatPercent
} from './sfaEnums';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';

// Progress indicator component
const TargetProgress = ({ actual, target, size = 'medium' }) => {
  const percent = target > 0 ? (actual / target) * 100 : 0;
  const clampedPercent = Math.min(percent, 100);
  
  let color = 'error';
  let icon = <ErrorIcon fontSize="small" />;
  
  if (percent >= 100) {
    color = 'success';
    icon = <CheckCircleIcon fontSize="small" />;
  } else if (percent >= 75) {
    color = 'warning';
    icon = <WarningIcon fontSize="small" />;
  } else if (percent >= 50) {
    color = 'info';
    icon = <TrendingUpIcon fontSize="small" />;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1, minWidth: size === 'large' ? 150 : 100 }}>
        <LinearProgress
          variant="determinate"
          value={clampedPercent}
          color={color}
          sx={{ height: size === 'large' ? 10 : 6, borderRadius: 1 }}
        />
      </Box>
      <Typography
        variant={size === 'large' ? 'body1' : 'body2'}
        fontWeight={500}
        color={`${color}.main`}
        sx={{ minWidth: 60, textAlign: 'right' }}
      >
        {formatPercent(percent)}%
      </Typography>
      <Tooltip title={percent >= 100 ? 'Target Met!' : `${formatPercent(100 - percent)}% to go`}>
        {icon}
      </Tooltip>
    </Box>
  );
};

const SalesTargets = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [recalculating, setRecalculating] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    period_type: '',
    year: '',
    user_id: '',
    team_id: ''
  });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [deletingTarget, setDeletingTarget] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    period_type: 'MONTHLY',
    period_start: startOfMonth(new Date()),
    period_end: endOfMonth(new Date()),
    target_type: 'REVENUE',
    target_amount: 0,
    user_id: null,
    team_id: null,
    notes: ''
  });

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  // Summary stats
  const [summary, setSummary] = useState({
    total_target: 0,
    total_actual: 0,
    targets_met: 0,
    targets_count: 0
  });

  // Check permissions
  useEffect(() => {
    const checkPerms = async () => {
      const viewPerm = await hasPermission('crm', 'view');
      const addPerm = await hasPermission('crm', 'add');
      const editPerm = await hasPermission('crm', 'edit');
      setCanView(viewPerm);
      setCanAdd(addPerm);
      setCanEdit(editPerm);
    };
    checkPerms();
  }, []);

  // Load initial data
  useEffect(() => {
    loadTargets();
    loadLookups();
  }, [page, rowsPerPage, filters]);

  // Load targets
  const loadTargets = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await salesTargetService.getAll(params);
      setTargets(response.targets || response.data || []);
      setTotalCount(response.total || response.pagination?.total || 0);

      // Calculate summary
      const targetsData = response.targets || response.data || [];
      const summaryData = {
        total_target: targetsData.reduce((sum, t) => sum + (t.target_amount || 0), 0),
        total_actual: targetsData.reduce((sum, t) => sum + (t.actual_amount || 0), 0),
        targets_met: targetsData.filter(t => (t.actual_amount || 0) >= (t.target_amount || 0)).length,
        targets_count: targetsData.length
      };
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading targets:', error);
      enqueueSnackbar('Failed to load sales targets', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load users and teams for lookup
  const loadLookups = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        axiosInstance.get('/api/crm/teams/available-managers'),  // Changed: Use same endpoint as SalesTeams
        axiosInstance.get('/api/crm/teams', { params: { limit: 100 } })
      ]);

      // Handle the available-managers response format
      const usersList = usersRes.data?.data || [];
      setUsers(Array.isArray(usersList) ? usersList : []);
      
      const teamsList = teamsRes.data?.teams || teamsRes.data?.data || [];
      setTeams(Array.isArray(teamsList) ? teamsList : []);
    } catch (error) {
      console.error('Error loading lookups:', error);
      setUsers([]);
      setTeams([]);
    }
  };

  // Handle period type change in form
  const handlePeriodTypeChange = (periodType) => {
    let periodStart, periodEnd;
    const now = new Date();

    switch (periodType) {
      case 'MONTHLY':
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
        break;
      case 'QUARTERLY':
        periodStart = startOfQuarter(now);
        periodEnd = endOfQuarter(now);
        break;
      case 'YEARLY':
        periodStart = startOfYear(now);
        periodEnd = endOfYear(now);
        break;
      default:
        periodStart = startOfMonth(now);
        periodEnd = endOfMonth(now);
    }

    setFormData(prev => ({
      ...prev,
      period_type: periodType,
      period_start: periodStart,
      period_end: periodEnd
    }));
  };

  // Open create dialog
  const openCreateDialog = () => {
    setEditingTarget(null);
    setFormData({
      name: '',
      period_type: 'MONTHLY',
      period_start: startOfMonth(new Date()),
      period_end: endOfMonth(new Date()),
      target_type: 'REVENUE',
      target_amount: 0,
      user_id: null,
      team_id: null,
      notes: ''
    });
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (target) => {
    setEditingTarget(target);
    setFormData({
      name: target.name || '',
      period_type: target.period_type || 'MONTHLY',
      period_start: target.period_start ? new Date(target.period_start) : startOfMonth(new Date()),
      period_end: target.period_end ? new Date(target.period_end) : endOfMonth(new Date()),
      target_type: target.target_type || 'REVENUE',
      target_amount: target.target_amount || 0,
      user_id: target.user_id || null,
      team_id: target.team_id || null,
      notes: target.notes || ''
    });
    setDialogOpen(true);
  };

  // Save target
  const handleSave = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Target name is required', { variant: 'warning' });
      return;
    }

    if (formData.target_amount <= 0) {
      enqueueSnackbar('Target amount must be greater than 0', { variant: 'warning' });
      return;
    }

    try {
      const payload = {
        ...formData,
        period_start: formData.period_start?.toISOString(),
        period_end: formData.period_end?.toISOString()
      };

      if (editingTarget) {
        await salesTargetService.update(editingTarget.id, payload);
        enqueueSnackbar('Target updated successfully', { variant: 'success' });
      } else {
        await salesTargetService.create(payload);
        enqueueSnackbar('Target created successfully', { variant: 'success' });
      }

      setDialogOpen(false);
      loadTargets();
    } catch (error) {
      console.error('Error saving target:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save target', { variant: 'error' });
    }
  };

  // Delete target
  const handleDelete = async () => {
    if (!deletingTarget) return;

    try {
      await salesTargetService.delete(deletingTarget.id);
      enqueueSnackbar('Target deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingTarget(null);
      loadTargets();
    } catch (error) {
      console.error('Error deleting target:', error);
      enqueueSnackbar('Failed to delete target', { variant: 'error' });
    }
  };

  // Recalculate actuals
  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await salesTargetService.recalculateActuals();
      enqueueSnackbar('Actuals recalculated successfully', { variant: 'success' });
      loadTargets();
    } catch (error) {
      console.error('Error recalculating:', error);
      enqueueSnackbar('Failed to recalculate actuals', { variant: 'error' });
    } finally {
      setRecalculating(false);
    }
  };

  // Get period display string
  const getPeriodDisplay = (target) => {
    if (!target.period_start || !target.period_end) return '-';

    const start = new Date(target.period_start);
    const end = new Date(target.period_end);

    switch (target.period_type) {
      case 'MONTHLY':
        return format(start, 'MMMM yyyy');
      case 'QUARTERLY':
        const quarter = Math.floor(start.getMonth() / 3) + 1;
        return `Q${quarter} ${format(start, 'yyyy')}`;
      case 'YEARLY':
        return format(start, 'yyyy');
      default:
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
  };

  // Get assignee display
  const getAssigneeDisplay = (target) => {
    if (target.user_name) {
      return (
        <Chip
          size="small"
          icon={<PersonIcon />}
          label={target.user_name}
          variant="outlined"
        />
      );
    }
    if (target.team_name) {
      return (
        <Chip
          size="small"
          icon={<GroupIcon />}
          label={target.team_name}
          variant="outlined"
          color="primary"
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<GroupIcon />}
        label="Company-wide"
        variant="outlined"
        color="secondary"
      />
    );
  };

  // Available years for filter
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Calculate overall attainment percentage
  const overallAttainment = summary.total_target > 0 
    ? (summary.total_actual / summary.total_target) * 100 
    : 0;

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view sales targets.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <FlagIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Sales Targets & Quotas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set and track sales targets for individuals, teams, and the organization
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={recalculating ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleRecalculate}
              disabled={recalculating}
            >
              Recalculate Actuals
            </Button>
            {canAdd && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
              >
                Create Target
              </Button>
            )}
          </Box>
        </Box>

        {/* Modern Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Total Target
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {formatCurrency(summary.total_target)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      All active targets
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56
                    }}
                  >
                    <MoneyIcon fontSize="large" />
                  </Avatar>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }}
              />
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Total Actual
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {formatCurrency(summary.total_actual)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Revenue achieved
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56
                    }}
                  >
                    <TrendingUpIcon fontSize="large" />
                  </Avatar>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }}
              />
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Overall Attainment
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {formatPercent(overallAttainment)}%
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Achievement rate
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56
                    }}
                  >
                    <SpeedIcon fontSize="large" />
                  </Avatar>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }}
              />
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Targets Met
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {summary.targets_met} / {summary.targets_count}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Goals achieved
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 56,
                      height: 56
                    }}
                  >
                    <CheckCircleIcon fontSize="large" />
                  </Avatar>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Period Type</InputLabel>
                <Select
                  value={filters.period_type}
                  label="Period Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, period_type: e.target.value }))}
                >
                  <MenuItem value="">All Periods</MenuItem>
                  {Object.entries(TARGET_PERIOD_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                >
                  {years.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>User</InputLabel>
                <Select
                  value={filters.user_id}
                  label="User"
                  onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value, team_id: '' }))}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Team</InputLabel>
                <Select
                  value={filters.team_id}
                  label="Team"
                  onChange={(e) => setFilters(prev => ({ ...prev, team_id: e.target.value, user_id: '' }))}
                >
                  <MenuItem value="">All Teams</MenuItem>
                  {teams.map(team => (
                    <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1}>
              <Button
                variant="text"
                onClick={() => setFilters({
                  period_type: '',
                  year: new Date().getFullYear(),
                  user_id: '',
                  team_id: ''
                })}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Targets Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading && <LinearProgress />}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Target Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Target</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actual</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 600 }}>Progress</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {targets.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <FlagIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" gutterBottom>
                        No sales targets found
                      </Typography>
                      {canAdd && (
                        <Button
                          startIcon={<AddIcon />}
                          onClick={openCreateDialog}
                          sx={{ mt: 2 }}
                        >
                          Create First Target
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  targets.map((target) => (
                    <TableRow key={target.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            variant="rounded"
                            sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}
                          >
                            <FlagIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {target.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {target.target_type}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{getAssigneeDisplay(target)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={TARGET_PERIOD_LABELS[target.period_type] || target.period_type}
                            variant="outlined"
                          />
                          <Typography variant="body2">{getPeriodDisplay(target)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {formatCurrency(target.target_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          {formatCurrency(target.actual_amount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TargetProgress
                          actual={target.actual_amount || 0}
                          target={target.target_amount}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {canEdit && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => openEditDialog(target)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEdit && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeletingTarget(target);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            {editingTarget ? 'Edit Sales Target' : 'Create Sales Target'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Target Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="e.g., Q1 2026 Revenue Target"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Period Type</InputLabel>
                  <Select
                    value={formData.period_type}
                    label="Period Type"
                    onChange={(e) => handlePeriodTypeChange(e.target.value)}
                  >
                    {Object.entries(TARGET_PERIOD_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Target Type</InputLabel>
                  <Select
                    value={formData.target_type}
                    label="Target Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, target_type: e.target.value }))}
                  >
                    <MenuItem value="REVENUE">Revenue</MenuItem>
                    <MenuItem value="DEALS_WON">Deals Won</MenuItem>
                    <MenuItem value="PIPELINE_VALUE">Pipeline Value</MenuItem>
                    <MenuItem value="NEW_CUSTOMERS">New Customers</MenuItem>
                    <MenuItem value="ACTIVITIES">Activities</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Period Start"
                  value={formData.period_start}
                  onChange={(date) => setFormData(prev => ({ ...prev, period_start: date }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Period End"
                  value={formData.period_end}
                  onChange={(date) => setFormData(prev => ({ ...prev, period_end: date }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Target Amount"
                  type="number"
                  value={formData.target_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_amount: parseFloat(e.target.value) || 0 }))}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Assign To (optional)
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(opt) => opt.name || opt.email || 'Unknown'}
                  value={users.find(u => u.user_id === formData.user_id) || null}
                  onChange={(e, user) => setFormData(prev => ({
                    ...prev,
                    user_id: user?.user_id || null,
                    team_id: user ? null : prev.team_id
                  }))}
                  disabled={!!formData.team_id}
                  renderInput={(params) => (
                    <TextField {...params} label="User" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={teams}
                  getOptionLabel={(opt) => opt.name}
                  value={teams.find(t => t.id === formData.team_id) || null}
                  onChange={(e, team) => setFormData(prev => ({
                    ...prev,
                    team_id: team?.id || null,
                    user_id: team ? null : prev.user_id
                  }))}
                  disabled={!!formData.user_id}
                  renderInput={(params) => (
                    <TextField {...params} label="Team" />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 1 }}>
                  Leave both User and Team empty for a company-wide target.
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  multiline
                  rows={2}
                  placeholder="Any additional notes about this target..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>
              {editingTarget ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Delete Sales Target</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the target "{deletingTarget?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default SalesTargets;